import * as THREE from 'three';
import {AbstractPointSnapStrategy} from './AbstractPointSnapStrategy';
import {PointSnapStrategyType} from './PointSnapStrategyType';
import type {PointSnapRequest, PointSnapResult} from './PointSnapTypes';
import {SketchRelationKind} from '../../sketch/solver/constraint/relation-compatibility/SketchRelationKinds';



/**
 * 水平/竖直捕捉策略。
 * 职责：
 * 1. 检测绘制方向是否接近水平或竖直（基于参考平面的 2D 坐标系）；
 * 2. 接近水平时将 rawPoint 投影到水平轴线上并返回为捕捉结果；
 * 3. 接近竖直时将 rawPoint 投影到竖直轴线上并返回为捕捉结果。
 * 说明：此策略无需管理场景对象，无需重写 clearPreview()。
 */
export class HorizontalVerticalPointSnapStrategy extends AbstractPointSnapStrategy {
    /** 策略类型。 */
    public readonly strategyType: PointSnapStrategyType = PointSnapStrategyType.HorizontalVertical;

    /**
     * 水平/竖直捕捉屏幕像素阈值。
     * 说明：约 ±5° 以内视为水平或竖直。
     */
    private static readonly HV_SNAP_SCREEN_DISTANCE_THRESHOLD_PX = 5;

    /**
     * 执行水平/竖直捕捉。
     * 说明：
     * 1. 绘制态使用 `drawingFirstPoint`，拖动直线端点使用 `directionalReferencePoints`；
     * 2. 将每个参考点和鼠标位置投影到参考平面 2D 坐标；
     * 3. 计算鼠标屏幕点到水平/竖直目标线段的像素距离，距离不超过 5px 时命中；
     * 4. 多条相邻直线或水平与竖直同时命中时，统一选择屏幕距离最近的一侧。
     * @param request 点捕捉请求参数。
     * @returns 捕捉结果；未命中时返回 null。
     */
    public resolve(request: PointSnapRequest): PointSnapResult | null {
        const referencePoints = request.directionalReferencePoints?.length
            ? request.directionalReferencePoints
            : request.drawingFirstPoint
                ? [request.drawingFirstPoint]
                : [];
        // 绘制和拖动链都未提供方向参考点时，不进行水平/竖直捕捉。
        if (referencePoints.length === 0) {
            return null;
        }

        // 获取当前参考平面。
        const referencePlane = this.viewer?.currentReferencePlane ?? null;
        if (!referencePlane) {
            return null;
        }

        let bestResult: PointSnapResult | null = null;
        let bestScreenDistance = Infinity;
        referencePoints.forEach((firstPoint) => {
            const candidate = this.resolveByReferencePoint(request, firstPoint);
            if (candidate && candidate[1] < bestScreenDistance) {
                bestResult = candidate[0];
                bestScreenDistance = candidate[1];
            }
        });
        return bestResult;
    }

    /**
     * 根据一个方向参考点计算水平或竖直捕捉候选。
     *
     * @param request 当前点捕捉请求。
     * @param firstPoint 当前候选直线保持不动的另一端点。
     * @returns 捕捉结果与对应屏幕距离；未命中时返回 null。
     */
    private resolveByReferencePoint(
        request: PointSnapRequest,
        firstPoint: THREE.Vector3,
    ): readonly [PointSnapResult, number] | null {
        const referencePlane = this.viewer?.currentReferencePlane ?? null;
        if (!referencePlane) {
            return null;
        }

        const rawPoint = request.worldPosition;

        // 将起点和鼠标位置投影到参考平面 2D 坐标系。
        const local1 = referencePlane.worldToLocal(firstPoint);
        const local2 = referencePlane.worldToLocal(rawPoint);

        const dx = local2.x - local1.x;
        const dy = local2.y - local1.y;
        const length = Math.sqrt(dx * dx + dy * dy);

        // 距离过短时不判断方向。
        if (length < 1e-6) {
            return null;
        }

        const screenStart = this.projectWorldToScreen(firstPoint);
        if (!screenStart) {
            return null;
        }

        const horizontalSnapLocal = new THREE.Vector2(local2.x, local1.y);
        const horizontalSnapWorld = referencePlane.localToWorld(horizontalSnapLocal);
        const horizontalScreenEnd = this.projectWorldToScreen(horizontalSnapWorld);
        const horizontalScreenDistance = horizontalScreenEnd
            ? AbstractPointSnapStrategy.pointToSegmentDistance(request.screenPosition, screenStart, horizontalScreenEnd)
            : null;

        const verticalSnapLocal = new THREE.Vector2(local1.x, local2.y);
        const verticalSnapWorld = referencePlane.localToWorld(verticalSnapLocal);
        const verticalScreenEnd = this.projectWorldToScreen(verticalSnapWorld);
        const verticalScreenDistance = verticalScreenEnd
            ? AbstractPointSnapStrategy.pointToSegmentDistance(request.screenPosition, screenStart, verticalScreenEnd)
            : null;

        const threshold = HorizontalVerticalPointSnapStrategy.HV_SNAP_SCREEN_DISTANCE_THRESHOLD_PX;
        const horizontalMatched = horizontalScreenDistance !== null && horizontalScreenDistance <= threshold;
        const verticalMatched = verticalScreenDistance !== null && verticalScreenDistance <= threshold;

        if (horizontalMatched && (!verticalMatched || (horizontalScreenDistance ?? Infinity) <= (verticalScreenDistance ?? Infinity))) {
            return [
                {
                    strategyType: this.strategyType,
                    position: horizontalSnapWorld,
                    indicatorPosition: horizontalSnapWorld,
                    targetObject: null,
                    weight: 700,
                    // 捕捉结果直接携带正式业务语义，直线提交无需解析图标资源路径。
                    suggestedRelationKind: SketchRelationKind.Horizontal,
                    // 明确标记为水平图标，避免依赖浮点数比较来区分 H/V。
                    indicatorIconOverride: '/textures/sketch/snap-horizontal.svg',
                },
                horizontalScreenDistance ?? Infinity,
            ];
        }

        if (verticalMatched) {
            return [
                {
                    strategyType: this.strategyType,
                    position: verticalSnapWorld,
                    indicatorPosition: verticalSnapWorld,
                    targetObject: null,
                    weight: 700,
                    // 捕捉结果直接携带正式业务语义，直线提交无需解析图标资源路径。
                    suggestedRelationKind: SketchRelationKind.Vertical,
                    // 明确标记为竖直图标，避免依赖浮点数比较来区分 H/V。
                    indicatorIconOverride: '/textures/sketch/snap-vertical.svg',
                },
                verticalScreenDistance ?? Infinity,
            ];
        }

        return null;
    }
}



